import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import type { Invoice, ListResponse, Vendor, VendorService } from "../../api/types";
import { useTenantCurrency } from "../../app/tenant";
import { formatMoney, formatPercent, formatQuantity } from "../../lib/format";
import {
  Card,
  CardHeader,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Modal,
  Notice,
} from "../../ui/components";
import { SkeletonTable, SkeletonText } from "../../ui/skeletons";
import { ContactPicker } from "../contacts/ContactPicker";

// What the engagements on this panel are for. The same panel is used from an event and from a hire,
// because the arrangement is the same one either way: somebody is hired in, and whoever is paying
// is the owner of the thing they were hired for.
export interface EngagementOwner {
  kind: "event" | "booking";
  id: string;
  reference: string;
  // Known already for a hire, which names its own customer. One of the client's own events may be
  // billed to anybody, so the dialog asks rather than guessing.
  customerId?: string | undefined;
}

interface DraftService {
  id?: string;
  vendorId: string;
  description: string;
  quantity: string;
  unit: string;
  unitAmount: string;
  taxRate: string;
  version: number;
}

const emptyDraft: DraftService = {
  vendorId: "",
  description: "",
  quantity: "1",
  unit: "each",
  unitAmount: "0",
  taxRate: "0",
  version: 0,
};

function draftFrom(service: VendorService): DraftService {
  return {
    id: service.id,
    vendorId: service.vendorId,
    description: service.description,
    quantity: String(service.quantityMilli / 1000),
    unit: service.unit,
    unitAmount: (service.unitAmountMinor / 100).toFixed(2),
    taxRate: (service.taxRateBasisPoints / 100).toFixed(2),
    version: service.version,
  };
}

export function VendorServicesPanel({
  owner,
  canManage,
  canBill,
}: {
  owner: EngagementOwner;
  canManage: boolean;
  canBill: boolean;
}) {
  const queryClient = useQueryClient();
  const currency = useTenantCurrency();
  // The same panel serves both sides of the building, and the money flows opposite ways:
  // an event's engagements are bills the client pays, a hire's engagements are recharged
  // to the hirer through the invoice. The wording and the controls follow the direction.
  const payables = owner.kind === "event";
  const [draft, setDraft] = useState<DraftService | null>(null);
  const [removing, setRemoving] = useState<VendorService | null>(null);
  const [billing, setBilling] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);
  const [billed, setBilled] = useState<Invoice | null>(null);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const ownerQuery = owner.kind === "event" ? `eventId=${owner.id}` : `bookingId=${owner.id}`;

  const services = useQuery({
    queryKey: ["vendor-services", owner.kind, owner.id],
    queryFn: () => api<ListResponse<VendorService>>(`/api/v1/vendor-services?${ownerQuery}`),
    enabled: Boolean(owner.id),
  });
  // Retired suppliers are left out because this list exists to be chosen from. The one already
  // named on an engagement still shows in the table, which is where history belongs.
  const vendors = useQuery({
    queryKey: ["vendors", "engagement-picker"],
    queryFn: () => api<ListResponse<Vendor>>("/api/v1/vendors"),
    enabled: canManage && draft !== null,
  });

  const items = services.data?.items ?? [];
  const unbilled = items.filter((service) => !service.invoiceId);
  const arranged = items.reduce((total, service) => total + service.grossMinor, 0);
  const paidTotal = items
    .filter((service) => service.paidAt)
    .reduce((total, service) => total + service.grossMinor, 0);

  async function saveDraft(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    if (!draft) return;
    const payload = {
      vendorId: draft.vendorId,
      ...(owner.kind === "event" ? { eventId: owner.id } : { bookingId: owner.id }),
      description: draft.description,
      quantityMilli: Math.round(Number(draft.quantity) * 1000),
      unit: draft.unit,
      unitAmountMinor: Math.round(Number(draft.unitAmount) * 100),
      taxRateBasisPoints: Math.round(Number(draft.taxRate) * 100),
      version: draft.version,
    };
    setWorking(true);
    setError(undefined);
    try {
      await api(draft.id ? `/api/v1/vendor-services/${draft.id}` : "/api/v1/vendor-services", {
        method: draft.id ? "PUT" : "POST",
        body: jsonBody(payload),
      });
      await queryClient.invalidateQueries({ queryKey: ["vendor-services"] });
      setDraft(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function remove() {
    if (!removing) return;
    setWorking(true);
    setError(undefined);
    try {
      // The version travels in the address because a DELETE carries no body. Without it an
      // engagement somebody else had just repriced would go on the strength of a stale screen.
      await api(`/api/v1/vendor-services/${removing.id}?version=${removing.version}`, {
        method: "DELETE",
      });
      await queryClient.invalidateQueries({ queryKey: ["vendor-services"] });
      setRemoving(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function setPaid(service: VendorService, paid: boolean) {
    setWorking(true);
    setError(undefined);
    try {
      await api(`/api/v1/vendor-services/${service.id}/paid`, {
        method: "PUT",
        body: jsonBody({ paid, version: service.version }),
      });
      await queryClient.invalidateQueries({ queryKey: ["vendor-services"] });
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function bill(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    if (owner.kind !== "booking") return;
    const data = new FormData(formEvent.currentTarget);
    const customerId = owner.customerId ?? String(data.get("customerId") ?? "");
    const dueAt = String(data.get("dueAt") ?? "");
    setWorking(true);
    setError(undefined);
    try {
      // Only the identifiers travel. What each engagement comes to is read from the engagement
      // itself inside the transaction, so the invoice bills what was agreed with the supplier
      // rather than what this screen last displayed, and the server prices it in the tenant
      // currency because the seller decides that.
      const invoice = await api<Invoice>("/api/v1/invoices", {
        method: "POST",
        body: jsonBody({
          bookingId: owner.id,
          customerId,
          dueAt: dueAt ? new Date(dueAt).toISOString() : null,
          serviceIds: chosen,
        }),
      });
      await queryClient.invalidateQueries({ queryKey: ["vendor-services"] });
      await queryClient.invalidateQueries({ queryKey: ["invoices"] });
      setBilled(invoice);
      setBilling(false);
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  return (
    <Card>
      <CardHeader
        title={payables ? "Suppliers and costs" : "Brought in"}
        description={
          payables
            ? "Suppliers engaged for this event and what each one costs. These are bills the client pays, tracked from agreed to settled."
            : "Suppliers engaged under this rental and what each one is charging. An engagement can be put on the renter's invoice once, and never twice."
        }
        actions={
          canManage ? (
            <div className="inline">
              {!payables && canBill && unbilled.length ? (
                <button
                  className="button secondary small"
                  type="button"
                  onClick={() => {
                    setError(undefined);
                    setBilled(null);
                    setChosen(unbilled.map((service) => service.id));
                    setBilling(true);
                  }}
                >
                  Bill engagements
                </button>
              ) : null}
              <button
                className="button primary small"
                type="button"
                onClick={() => {
                  setError(undefined);
                  setDraft(emptyDraft);
                }}
              >
                <Plus size={16} aria-hidden="true" /> Add engagement
              </button>
            </div>
          ) : null
        }
      />
      {error ? <ErrorPanel error={error} /> : null}
      {billed ? (
        <Notice tone="success" title={`Invoice ${billed.number} created`}>
          The engagements on it now name that document and cannot be billed again.{" "}
          <Link to="/finance">Open finance</Link> to issue it.
        </Notice>
      ) : null}
      {services.isLoading ? <SkeletonTable bare columns={7} rows={3} /> : null}
      {services.error ? <ErrorPanel error={services.error} /> : null}
      {!services.isLoading && !services.error ? (
        items.length ? (
          <>
            <DataTable
              caption="Suppliers brought in for this"
              items={items}
              getKey={(service: VendorService) => service.id}
              defaultSort={{ label: "Supplier", direction: "ascending" }}
              columns={[
                {
                  label: "Supplier",
                  render: (service) => service.vendorName,
                  sortValue: (service) => service.vendorName,
                },
                { label: "What", render: (service) => service.description },
                {
                  label: "Quantity",
                  render: (service) => `${formatQuantity(service.quantityMilli)} ${service.unit}`,
                },
                {
                  label: "Unit price",
                  render: (service) => formatMoney(service.unitAmountMinor, currency),
                  sortValue: (service) => service.unitAmountMinor,
                },
                { label: "Tax", render: (service) => formatPercent(service.taxRateBasisPoints) },
                {
                  label: "Total",
                  render: (service) => formatMoney(service.grossMinor, currency),
                  sortValue: (service) => service.grossMinor,
                },
                ...(payables
                  ? [
                      {
                        label: "Paid",
                        render: (service: VendorService) =>
                          canManage ? (
                            <button
                              className={`button ${service.paidAt ? "secondary" : "primary"} small`}
                              type="button"
                              disabled={working}
                              onClick={() => void setPaid(service, !service.paidAt)}
                            >
                              {service.paidAt ? "Mark unpaid" : "Mark paid"}
                            </button>
                          ) : service.paidAt ? (
                            "Paid"
                          ) : (
                            <span className="muted">Not yet</span>
                          ),
                        sortValue: (service: VendorService) => (service.paidAt ? 1 : 0),
                      },
                    ]
                  : [
                      {
                        label: "Billed",
                        render: (service: VendorService) =>
                          service.invoiceNumber ? (
                            <Link to="/finance">{service.invoiceNumber}</Link>
                          ) : (
                            <span className="muted">Not yet</span>
                          ),
                        sortValue: (service: VendorService) => service.invoiceNumber ?? "",
                      },
                    ]),
                ...(canManage
                  ? [
                      {
                        label: "Actions",
                        render: (service: VendorService) =>
                          service.invoiceId ? (
                            <span className="muted">On an invoice</span>
                          ) : (
                            <div className="inline">
                              <button
                                className="button ghost small"
                                type="button"
                                onClick={() => {
                                  setError(undefined);
                                  setDraft(draftFrom(service));
                                }}
                              >
                                Edit
                              </button>
                              <button
                                className="button ghost small"
                                type="button"
                                onClick={() => {
                                  setError(undefined);
                                  setRemoving(service);
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          ),
                      },
                    ]
                  : []),
              ]}
            />
            <p className="muted">
              {payables
                ? `${formatMoney(arranged, currency)} agreed with suppliers, ${formatMoney(paidTotal, currency)} of it paid.`
                : `${formatMoney(arranged, currency)} arranged with suppliers, ${unbilled.length} engagement${unbilled.length === 1 ? "" : "s"} not yet on an invoice.`}
            </p>
          </>
        ) : (
          <EmptyState
            title="Nobody brought in yet"
            description={
              payables
                ? "Caterers, sound companies and other suppliers engaged for this event appear here, priced at what they will cost the client."
                : "Suppliers arranged under this rental appear here, priced at what the renter will be billed for them."
            }
          />
        )
      ) : null}

      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? "Change this engagement" : "Bring a supplier in"}
        description={
          draft?.id
            ? "What the engagement is for does not change here. Moving one between an event and a rental moves whose bill it lands on."
            : payables
              ? `A cost against ${owner.reference}. The figures here are what the client has agreed to pay the supplier.`
              : `Charged against ${owner.reference}. The figures here are what the renter's invoice will carry.`
        }
      >
        {vendors.isLoading ? <SkeletonText lines={2} /> : null}
        {vendors.error ? <ErrorPanel error={vendors.error} /> : null}
        {draft ? (
          <form className="form-stack" onSubmit={saveDraft}>
            <Field
              label="Supplier"
              hint="Retired suppliers keep their history and stop being offered."
            >
              <select
                data-autofocus
                required
                value={draft.vendorId}
                onChange={(event) => setDraft({ ...draft, vendorId: event.target.value })}
              >
                <option value="">Choose a supplier</option>
                {(vendors.data?.items ?? []).map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="What they are doing">
              <input
                required
                maxLength={240}
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </Field>
            <div className="form-grid">
              <Field label="Quantity">
                <input
                  required
                  type="number"
                  min="0.001"
                  step="0.001"
                  value={draft.quantity}
                  onChange={(event) => setDraft({ ...draft, quantity: event.target.value })}
                />
              </Field>
              <Field label="Unit" hint="Covers, hours, packages, whatever they charge by.">
                <input
                  required
                  maxLength={24}
                  value={draft.unit}
                  onChange={(event) => setDraft({ ...draft, unit: event.target.value })}
                />
              </Field>
              <Field label="Price per unit">
                <input
                  required
                  type="number"
                  min="0"
                  step="0.01"
                  value={draft.unitAmount}
                  onChange={(event) => setDraft({ ...draft, unitAmount: event.target.value })}
                />
              </Field>
              <Field
                label="Tax rate"
                hint="A percentage. Leave at zero when the supplier does not charge it."
              >
                <input
                  required
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={draft.taxRate}
                  onChange={(event) => setDraft({ ...draft, taxRate: event.target.value })}
                />
              </Field>
            </div>
            <FormActions
              submitting={working}
              submitLabel={draft.id ? "Save the engagement" : "Add the engagement"}
              onCancel={() => setDraft(null)}
            />
          </form>
        ) : null}
      </Modal>

      <Modal
        open={billing}
        onClose={() => setBilling(false)}
        title="Bill these engagements"
        description={`One draft invoice against ${owner.reference}, priced from the engagements themselves and charged to whoever took the room.`}
      >
        <form className="form-stack" onSubmit={bill}>
          {owner.customerId ? null : (
            <Field label="Bill to" hint="The renter these arranged services are charged to.">
              <ContactPicker autoFocus name="customerId" required />
            </Field>
          )}
          <Field label="Due date" hint="Optional. Left empty the invoice carries no due date.">
            <input type="date" name="dueAt" />
          </Field>
          <fieldset className="form-stack">
            <legend>Engagements to bill</legend>
            {unbilled.map((service) => (
              <label className="check-field" key={service.id}>
                <input
                  type="checkbox"
                  checked={chosen.includes(service.id)}
                  onChange={(event) =>
                    setChosen((current) =>
                      event.target.checked
                        ? [...current, service.id]
                        : current.filter((id) => id !== service.id),
                    )
                  }
                />
                <span>
                  {service.vendorName} - {service.description} (
                  {formatMoney(service.grossMinor, currency)})
                </span>
              </label>
            ))}
          </fieldset>
          <Notice tone="info">
            {chosen.length} engagement{chosen.length === 1 ? "" : "s"} totaling{" "}
            {formatMoney(
              unbilled
                .filter((service) => chosen.includes(service.id))
                .reduce((total, service) => total + service.grossMinor, 0),
              currency,
            )}
            . The invoice is created as a draft, so it can be checked before anybody is sent it.
          </Notice>
          <FormActions
            submitting={working}
            disabled={chosen.length === 0}
            submitLabel="Create the invoice"
            onCancel={() => setBilling(false)}
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={removing !== null}
        title="Remove this engagement"
        description={
          removing
            ? `${removing.vendorName} will no longer be recorded as brought in for ${owner.reference}.`
            : ""
        }
        confirmLabel="Remove it"
        submitting={working}
        onClose={() => setRemoving(null)}
        onConfirm={() => void remove()}
      />
    </Card>
  );
}
